import gql from 'graphql-tag';

import { apolloClient } from '@/apollo/apollo';
import * as appColors from '@/functions/appColors';
import * as actions from '@/constants/actions';

export default {
    mounted() {
        this.initialize();
    },
    data() {
        return {
            queryDone: false,
            queryData: {},
            tableData: [],
            dataQuery: gql`query stationStreamflow($id: Int!, $type: Int!) {
                getStation(id: $id) {
                    name
                    nid
                    net
                    yr
                    ty
                    description
                    area
                    mean_annual_discharge
                    flow_metrics
                    licence_link
                    formatted_seven_day_flow_chart_data
                    formatted_stage_chart_data
                    seven_day_flow {
                        current
                        historical
                    }
                    monthly_mean_flow(type: $type) {
                        current
                        yearly
                    }
                    stage {
                        current
                        historical
                        yearly
                    }
                }
            }`,
            properties: [],
            sevenDayFlowDatasets: null,
            stageDatasets: null,
        };
    },
    methods: {
        /**
         * handle initial data fetching and potentially station changes
         */
        async initialize() {
            // reset local vars
            this.queryDone = false;
            this.queryData = {};

            this.sevenDayFlowDatasets = null;
            this.stageDatasets = null;

            if (!this.station) return;

            // fetch data
            await this.getData();
            this.formatTableData();
            this.buildProps();
        },
        /**
         * query the API and fetch station data
         */
        async getData() {
            if (!this.station) return;
            this.$store.dispatch(actions.ADD_LOADING, 'load station data');
            try {
                const { data } = await apolloClient.query({
                    query: this.dataQuery,
                    variables: {
                        id: this.station.id,
                        type: 1,
                    },
                });

                // populate variables
                this.buildDatasets(data.getStation);
                this.queryData = data.getStation;

                this.queryDone = true;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving station data');
            }
            this.$store.dispatch(actions.REMOVE_LOADING, 'load station data');
        },
        /**
         * build an array of properties to display
         */
        buildProps() {
            const data = this.queryData;
            const numFormat = this.$options.filters.numFormat;
            const properties = [];

            properties.push({
                title: 'Description',
                value: data.description,
            });

            properties.push({
                title: 'Date Range',
                value: `${this.years[0]}-${this.years[1]}`,
            });

            if (data.area) {
                const area = numFormat(data.area, '0,0.[00]');
                properties.push({
                    title: 'Drainage Area',
                    value: `${area} km²`,
                });
            }

            if (data.mean_annual_discharge) {
                let mad = data.mean_annual_discharge;
                let format = '0,0.[00]';
                // extra decimal places for small values
                if (mad < 1) {
                    format = '0,0.[000]';
                }
                mad = numFormat(data.mean_annual_discharge, format);
                properties.push({
                    title: 'Mean Annual Discharge',
                    value: `${mad} m³/s`,
                });
            }

            this.properties = properties;
        },
        /**
         *
         * @return {{headers: *[], dataMap: {avg: {}, max: {}, min: {}}}}
         */
        async formatTableData() {
            if (!this.station) return;
            this.tableData = await this.$worker.run((yearly, current) => {
                const headers = [
                    { text: 'Year' },
                    { text: 'Jan' },
                    { text: 'Feb' },
                    { text: 'Mar' },
                    { text: 'Apr' },
                    { text: 'May' },
                    { text: 'Jun' },
                    { text: 'Jul' },
                    { text: 'Aug' },
                    { text: 'Sep' },
                    { text: 'Oct' },
                    { text: 'Nov' },
                    { text: 'Dec' },
                ];

                const items = [];

                let currentMean = ['Mean'].concat(current.map(e => e.avg));
                currentMean = currentMean.map(e => ({ text: e, value: e }));
                let currentMax = ['Maximum'].concat(current.map(e => e.max));
                currentMax = currentMax.map(e => ({ text: e, value: e }));
                let currentMin = ['Minimum'].concat(current.map(e => e.min));
                currentMin = currentMin.map(e => ({ text: e, value: e }));

                items.push(currentMean, currentMax, currentMin);

                const currentMap = {
                    avg: {
                        year: 'Mean',
                    },
                    max: {
                        year: 'Maximum',
                    },
                    min: {
                        year: 'Minimum',
                    },
                };

                current.forEach((item) => {
                    currentMap.avg[item.m] = item.avg;
                    currentMap.max[item.m] = item.max;
                    currentMap.min[item.m] = item.min;
                });

                const yearlyMap = {};
                yearly.forEach((item) => {
                    const { year, m, v, days, qa, full_month } = item;
                    if (yearlyMap[year] === undefined) {
                        yearlyMap[year] = [{ text: year }];
                    }

                    let text = v;
                    if (v || v === 0) {
                        text = String(v);
                        if (qa === false) {
                            text += '*';
                        }
                        if (full_month === false) { // eslint-disable-line camelcase
                            text += `&nbsp;<sup>(${days})</sup>`;
                        }
                    }

                    yearlyMap[year][m] = {
                        text,
                        value: v,
                        days,
                        qa,
                        full_month,
                    };
                });

                // Set data
                const yearlyItems = self.Object.values(yearlyMap);
                return [].concat([headers], items, yearlyItems);
            }, [
                this.queryData.monthly_mean_flow.yearly,
                this.queryData.monthly_mean_flow.current,
            ]);
        },
        buildDatasets(data) {
            // reduce number of data points to plot on the spark line
            const sevenDayFlowHistorical = data.seven_day_flow.historical;
            const stageHistorical = data.stage.historical;
            // const sevenDayFlowCurrent = data.seven_day_flow.current;

            this.sevenDayFlowDatasets = [
                {
                    label: 'Historical 50th %ile',
                    data: sevenDayFlowHistorical.map(d => d.p50),
                    borderColor: appColors.sparklineColor2,
                    borderWidth: 1,
                },
                {
                    label: 'Historical Max',
                    data: sevenDayFlowHistorical.map(d => d.max),
                    backgroundColor: appColors.sparklineBgColor,
                    borderColor: 'rgba(0,0,0,0)', // transparent
                },
                {
                    label: 'Historical Min',
                    data: sevenDayFlowHistorical.map(d => d.min),
                    backgroundColor: appColors.sparklineBgColor,
                    borderColor: 'rgba(0,0,0,0)', // transparent
                    fill: '-1',
                },
            ];
            this.stageDatasets = [
                {
                    label: 'Historical 50th %ile',
                    data: stageHistorical.map(d => d.a),
                    borderColor: appColors.sparklineColor2,
                    borderWidth: 1,
                },
                {
                    label: 'Historical Max',
                    data: stageHistorical.map(d => d.max),
                    backgroundColor: appColors.sparklineBgColor,
                    borderColor: 'rgba(0,0,0,0)', // transparent
                },
                {
                    label: 'Historical Min',
                    data: stageHistorical.map(d => d.min),
                    backgroundColor: appColors.sparklineBgColor,
                    borderColor: 'rgba(0,0,0,0)', // transparent
                    fill: '-1',
                },
            ];
        },
    },
    computed: {
        station() {
            return this.$store.state.map.selectedStation;
        },
        name() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.name;
        },
        networkUrl() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.licence_link;
        },
        network() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.net;
        },
        networkId() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.nid;
        },
        type() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.ty;
        },
        years() {
            if (!this.queryDone) {
                return [];
            }
            return this.queryData.yr;
        },
        numYears() {
            if (this.years.length === 2) {
                const years = this.years[1] - this.years[0];
                return years + 1;
            }
            return 0;
        },
    },
    watch: {
        station() {
            // re-initialize component when a new station is selected
            this.initialize();
        },
    },
};



// WEBPACK FOOTER //
// ./src/mixins/streamflowData.js